import React, { useContext, useEffect, useMemo, useState } from 'react';

import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  TextField,
  IconButton,
  Divider,
  Grid,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
} from '@material-ui/core';
import { ToggleButton, ToggleButtonGroup } from '@material-ui/lab';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import DeleteIcon from '@material-ui/icons/Delete';
import SwapHorizIcon from '@material-ui/icons/SwapHoriz';

import {
  getUnitConversions,
  createUnitConversion,
  deleteUnitConversion,
  getMaterials,
  getUom,
} from '../../../Actions';
import { AlertContext } from '../../../Context/AlertContextProvider';

// The stored conversion_factor is a straight price multiplier:
//   price per <target> = price per <source> × factor
// For roll goods the two directions are fixed: 1 SY = 9 SF.
//   Show in SF (costs arrive per SY):  SY → SF, factor 1/9
//   Show in SY (costs arrive per SF):  SF → SY, factor 9
const ROLL_GOODS_TEMPLATES = {
  SF: { source: 'SY', target: 'SF', factor: 0.11111, note: 'costs per SY ÷ 9' },
  SY: { source: 'SF', target: 'SY', factor: 9, note: 'costs per SF × 9' },
};

const formatFactor = (f) => String(Number(f));

const UnitConversions = () => {
  const { handleAlertOpen, setMessage, setMessageType } =
    useContext(AlertContext);

  const [rules, setRules] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [uoms, setUoms] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  // Builder state
  const [mode, setMode] = useState('roll'); // 'roll' (templated SF/SY) | 'custom'
  const [materialClassCd, setMaterialClassCd] = useState('');
  const [displayUom, setDisplayUom] = useState(''); // roll mode: 'SF' | 'SY'
  const [sourceUom, setSourceUom] = useState(''); // custom mode
  const [targetUom, setTargetUom] = useState(''); // custom mode
  const [manualFactor, setManualFactor] = useState('1'); // custom mode
  const [samplePrice, setSamplePrice] = useState('18.00');

  const loadRules = () => {
    getUnitConversions()
      .then((result) => setRules(result.data.data ?? []))
      .catch(() => setRules([]));
  };

  useEffect(() => {
    loadRules();
    getMaterials()
      .then((result) => setMaterials(result.data.data ?? []))
      .catch(() => setMaterials([]));
    getUom()
      .then((result) => setUoms(result.data.data ?? []))
      .catch(() => setUoms([]));
  }, []);

  // Resolve the rule the builder currently describes, whichever mode is active.
  const template = mode === 'roll' ? ROLL_GOODS_TEMPLATES[displayUom] : null;
  const effSource = mode === 'roll' ? template?.source : sourceUom;
  const effTarget = mode === 'roll' ? template?.target : targetUom;
  const effFactor =
    mode === 'roll'
      ? template?.factor ?? null
      : manualFactor !== '' && Number(manualFactor) > 0
        ? Number(manualFactor)
        : null;

  const sample = Number(samplePrice);
  const converted =
    effFactor != null && isFinite(sample) && sample > 0
      ? (sample * effFactor).toFixed(2)
      : null;

  const canAdd =
    materialClassCd && effSource && effTarget && effSource !== effTarget && effFactor != null;

  const resetBuilder = () => {
    setMaterialClassCd('');
    setDisplayUom('');
    setSourceUom('');
    setTargetUom('');
    setManualFactor('1');
  };

  const handleAdd = async () => {
    setSaving(true);
    try {
      await createUnitConversion(materialClassCd, effSource, effTarget, effFactor);
      setMessage('Unit conversion saved');
      setMessageType('success');
      handleAlertOpen();
      resetBuilder();
      loadRules();
    } catch (err) {
      setMessage(err?.response?.data?.message || 'Error saving unit conversion');
      setMessageType('error');
      handleAlertOpen();
    }
    setSaving(false);
  };

  const handleDelete = async (rule) => {
    if (
      !window.confirm(
        `Remove the ${rule.material_class || rule.material_class_cd} conversion (${rule.source_uom_type_cd} → ${rule.target_uom_type_cd})? Pricing goes back to the cost's own unit.`
      )
    ) {
      return;
    }
    try {
      await deleteUnitConversion(rule.unit_conversion_id);
      setMessage('Unit conversion removed');
      setMessageType('success');
      handleAlertOpen();
      loadRules();
    } catch (err) {
      setMessage('Error removing unit conversion');
      setMessageType('error');
      handleAlertOpen();
    }
  };

  const ruleSentence = (rule) => (
    <>
      <b>{rule.material_class || rule.material_class_cd}</b>: cost per{' '}
      <b>{rule.source_uom_type_cd}</b> → sold per <b>{rule.target_uom_type_cd}</b>{' '}
      (× {formatFactor(rule.conversion_factor)})
    </>
  );

  const uomOptions = useMemo(
    () => [...uoms].sort((a, b) => (a.uom_type_cd > b.uom_type_cd ? 1 : -1)),
    [uoms]
  );

  const renderPreview = () =>
    effSource && effTarget && effFactor != null ? (
      <div
        style={{
          marginTop: 24,
          padding: '14px 18px',
          backgroundColor: '#fff6ef',
          borderLeft: '3px solid #FF682C',
          borderRadius: 4,
        }}
      >
        <Typography variant="body2" style={{ marginBottom: 8 }}>
          Costs arriving per <b>{effSource}</b> will be shown per{' '}
          <b>{effTarget}</b> — factor <b>{formatFactor(effFactor)}</b>
          {template ? ` (${template.note})` : ''}
        </Typography>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <Typography variant="body2">Example: $</Typography>
          <TextField
            size="small"
            value={samplePrice}
            onChange={(e) => setSamplePrice(e.target.value)}
            style={{ width: 80 }}
            inputProps={{ style: { padding: '4px 8px' } }}
          />
          <Typography variant="body2">
            per <b>{effSource}</b> becomes{' '}
            <b>{converted !== null ? `$${converted}` : '—'}</b> per{' '}
            <b>{effTarget}</b>
          </Typography>
        </div>
      </div>
    ) : null;

  return (
    // Fill the fixed-height card so the info accordion pins to the bottom edge
    // like the other Cost Adjustment cards.
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <div className="mnd__header">
        <Typography className="mnd__header-text" variant="h4">
          Unit Conversions
        </Typography>
      </div>
      <div style={{ padding: '16px 24px', textAlign: 'center', flex: 1, minHeight: 0, overflowY: 'auto' }}>
        {rules.length ? (
          <div style={{ textAlign: 'left', marginBottom: 12 }}>
            {rules.map((rule) => (
              <Typography key={rule.unit_conversion_id} variant="body2" style={{ marginBottom: 4 }}>
                <SwapHorizIcon style={{ fontSize: 16, verticalAlign: 'text-bottom', color: '#FF682C' }} />{' '}
                {ruleSentence(rule)}
              </Typography>
            ))}
          </div>
        ) : (
          <Typography variant="body2" color="textSecondary" style={{ marginBottom: 12 }}>
            No conversions set — products sell in the same unit their cost
            arrives in.
          </Typography>
        )}
        <Button variant="contained" onClick={() => setDialogOpen(true)}>
          Manage Conversions
        </Button>
      </div>
      <Accordion style={{ marginTop: 'auto' }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h6">Unit Conversions</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Typography>
            Roll goods like carpet and pad often arrive costed per square yard
            but sell per square foot (or the reverse). A conversion re-prices
            and displays those products in the unit you sell.
          </Typography>
        </AccordionDetails>
      </Accordion>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} fullWidth maxWidth="md">
        <DialogTitle>
          Unit Conversions
          <Typography variant="body2" color="textSecondary" style={{ marginTop: 4 }}>
            Choose a material class and the unit you sell in — the math is done
            for you and proven with an example before you save.
          </Typography>
        </DialogTitle>
        <DialogContent dividers style={{ padding: '24px 32px' }}>
          <ToggleButtonGroup
            value={mode}
            exclusive
            size="small"
            onChange={(e, v) => {
              if (v) setMode(v);
            }}
            style={{ marginBottom: 24 }}
          >
            <ToggleButton value="roll">Roll Goods (SF / SY)</ToggleButton>
            <ToggleButton value="custom">Custom</ToggleButton>
          </ToggleButtonGroup>

          {mode === 'roll' ? (
            <Grid container spacing={3} alignItems="center">
              <Grid item xs={12} sm={6}>
                <FormControl variant="outlined" size="small" fullWidth>
                  <InputLabel id="uc-class-label">Material class</InputLabel>
                  <Select
                    labelId="uc-class-label"
                    label="Material class"
                    value={materialClassCd}
                    onChange={(e) => setMaterialClassCd(e.target.value)}
                  >
                    {materials.map((m) => (
                      <MenuItem key={m.material_class_cd} value={m.material_class_cd}>
                        {m.material_class}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="textSecondary" style={{ display: 'block', marginBottom: 4 }}>
                  Sell &amp; display in
                </Typography>
                <ToggleButtonGroup
                  value={displayUom}
                  exclusive
                  size="small"
                  onChange={(e, v) => {
                    if (v) setDisplayUom(v);
                  }}
                >
                  <ToggleButton value="SF" style={{ width: 130 }}>
                    Square Feet
                  </ToggleButton>
                  <ToggleButton value="SY" style={{ width: 130 }}>
                    Square Yards
                  </ToggleButton>
                </ToggleButtonGroup>
              </Grid>
            </Grid>
          ) : (
            <Grid container spacing={3}>
              <Grid item xs={12} sm={4}>
                <FormControl variant="outlined" size="small" fullWidth>
                  <InputLabel id="uc-class-label-c">Material class</InputLabel>
                  <Select
                    labelId="uc-class-label-c"
                    label="Material class"
                    value={materialClassCd}
                    onChange={(e) => setMaterialClassCd(e.target.value)}
                  >
                    {materials.map((m) => (
                      <MenuItem key={m.material_class_cd} value={m.material_class_cd}>
                        {m.material_class}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={4}>
                <FormControl variant="outlined" size="small" fullWidth>
                  <InputLabel id="uc-source-label">Cost arrives per</InputLabel>
                  <Select
                    labelId="uc-source-label"
                    label="Cost arrives per"
                    value={sourceUom}
                    onChange={(e) => setSourceUom(e.target.value)}
                  >
                    {uomOptions.map((u) => (
                      <MenuItem key={u.uom_type_cd} value={u.uom_type_cd}>
                        {u.uom_type_cd} — {u.uom_type}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={4}>
                <FormControl variant="outlined" size="small" fullWidth>
                  <InputLabel id="uc-target-label">Sell &amp; display per</InputLabel>
                  <Select
                    labelId="uc-target-label"
                    label="Sell & display per"
                    value={targetUom}
                    onChange={(e) => setTargetUom(e.target.value)}
                  >
                    {uomOptions
                      .filter((u) => u.uom_type_cd !== sourceUom)
                      .map((u) => (
                        <MenuItem key={u.uom_type_cd} value={u.uom_type_cd}>
                          {u.uom_type_cd} — {u.uom_type}
                        </MenuItem>
                      ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  variant="outlined"
                  size="small"
                  type="number"
                  fullWidth
                  label="Conversion factor"
                  helperText={
                    sourceUom && targetUom
                      ? `price per ${sourceUom} × factor = price per ${targetUom}`
                      : 'price per source unit × factor = price per selling unit'
                  }
                  value={manualFactor}
                  onChange={(e) => setManualFactor(e.target.value)}
                />
              </Grid>
            </Grid>
          )}

          {renderPreview()}

          <div style={{ textAlign: 'right', marginTop: 24 }}>
            <Button
              variant="contained"
              className="admin__tax-update-btn"
              onClick={handleAdd}
              disabled={!canAdd || saving}
            >
              {saving ? 'Saving…' : 'Add Conversion'}
            </Button>
          </div>

          <Divider style={{ margin: '24px 0' }} />
          <Typography variant="subtitle2">Active conversions</Typography>
          {rules.length ? (
            <List dense>
              {rules.map((rule) => (
                <ListItem key={rule.unit_conversion_id} disableGutters>
                  <ListItemText
                    primary={ruleSentence(rule)}
                    secondary={`$${(Number(samplePrice) || 18).toFixed(2)} per ${rule.source_uom_type_cd} → $${(
                      (Number(samplePrice) || 18) * Number(rule.conversion_factor)
                    ).toFixed(2)} per ${rule.target_uom_type_cd}`}
                  />
                  <ListItemSecondaryAction>
                    <IconButton edge="end" size="small" onClick={() => handleDelete(rule)}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </ListItemSecondaryAction>
                </ListItem>
              ))}
            </List>
          ) : (
            <Typography variant="body2" color="textSecondary" style={{ marginTop: 8 }}>
              None yet.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default UnitConversions;
